import type { components } from '../../api/generated/schema'

export type Category = components['schemas']['CategoryResponse']
export type CategoryNode = { category: Category; children: Category[] }
export type Segment = { type: Category['category_type']; nodes: CategoryNode[] }

/** The one wording of a category type, singular: every screen that names a
 *  type reads this, so the copies cannot drift apart. */
export const CATEGORY_TYPE_LABEL = { expense: 'Expense', income: 'Income', transfer: 'Transfer' } as const

/** Fixed, so the screen's reading order does not depend on what the server
 *  happened to return first. Expense leads because it is 12 of the 14 groups. */
const ORDER = ['expense', 'income', 'transfer'] as const

const byOrderThenName = (a: Category, b: Category) =>
  a.display_order - b.display_order || a.name.localeCompare(b.name)

/**
 * Flat `CategoryResponse[]` → segments of two-level trees.
 *
 * `GET /categories` sorts by `display_order, name` across the whole set, so a
 * child does not follow its parent and the tree has to be assembled here.
 *
 * **A child whose parent is absent is promoted to the top level**, never
 * dropped. `updated_since` returns user categories only, so a legitimate
 * response can reference a system parent it does not include — and a silently
 * disappearing category is the worst available failure.
 *
 * **Grouping is by a row's own `category_type`**, not its parent's. The seed
 * keeps them aligned today; nothing enforces that, and inheriting the parent's
 * type would file a transfer under Expense the day it stops holding.
 */
export function buildTree(categories: Category[]): Segment[] {
  const ids = new Set(categories.map((c) => c.id))
  const isRoot = (c: Category) => c.parent_id == null || !ids.has(c.parent_id)

  const childrenOf = new Map<string, Category[]>()
  for (const c of categories) {
    if (isRoot(c)) continue
    const siblings = childrenOf.get(c.parent_id as string) ?? []
    siblings.push(c)
    childrenOf.set(c.parent_id as string, siblings)
  }

  return ORDER.flatMap((type) => {
    const nodes = categories
      .filter((c) => c.category_type === type && isRoot(c))
      .sort(byOrderThenName)
      .map((category) => ({
        category,
        children: (childrenOf.get(category.id) ?? [])
          .filter((c) => c.category_type === type)
          .sort(byOrderThenName),
      }))

    // A child under a parent of a different type is a root of its own segment.
    const strays = categories
      .filter((c) => c.category_type === type && !isRoot(c))
      .filter((c) => !nodes.some((n) => n.children.includes(c)))
      .sort(byOrderThenName)
      .map((category) => ({ category, children: [] }))

    const all = [...nodes, ...strays].sort((a, b) => byOrderThenName(a.category, b.category))
    return all.length > 0 ? [{ type, nodes: all }] : []
  })
}
